/**
 * IdentityPrompt — "which account is this?" for a browser-lane connection
 * that never recorded one (2026-09-03 owner directive: the identity "needs to
 * be displayed" on both onboarding and the integrations page).
 *
 * WHY IT EXISTS
 * -------------
 * A direct (OAuth) connection carries its identity from the provider's own
 * callback. A browser-lane one has no callback to read: the user signed into
 * someone else's website inside Prest0n's browser, and nothing in that
 * exchange tells us which account it was. The sign-in surface has offered an
 * optional field for it since 2026-08-18, and in practice it is left empty —
 * so most browser-lane tiles say nothing at all about which account they are,
 * and a user with two accounts on one app cannot tell them apart.
 *
 * The owner's decision (2026-09-03) was to ASK, rather than show nothing or
 * guess. This is that ask, in the one place its behaviour is defined, used by
 * both surfaces — two implementations of the same question is how the two
 * pages start giving different answers to it.
 *
 * WHAT IT PROMISES
 * ----------------
 *  - It is never a gate. Dismissing it costs nothing and it does not come
 *    back within the session; a user trying to finish onboarding is not made
 *    to answer a question about an app that already works.
 *  - It never guesses. The value written is the one the user typed. (The
 *    sign-in surface separately OFFERS the account email as one tap at the
 *    moment of signing in — this is the same posture, later.)
 *  - A failed save says so and keeps what was typed, rather than silently
 *    discarding it.
 */

import React, { useCallback, useRef, useState } from 'react';

/** Code-authored UI copy, {en, es} like every other line these surfaces
 *  author. "Prest0n" keeps its capital P (2026-08-22 naming rule). */
// copy: connect_identity_prompt_chrome
export const IDENTITY_PROMPT_COPY = {
  ask: { en: 'which account?', es: '¿qué cuenta?' },
  placeholder: { en: 'email or username', es: 'correo o usuario' },
  save: { en: 'save', es: 'guardar' },
  saving: { en: 'saving…', es: 'guardando…' },
  cancel: { en: 'not now', es: 'ahora no' },
  failed: {
    en: "couldn't save that just now — try again",
    es: 'no se pudo guardar — inténtalo otra vez',
  },
} as const;

export type IdentityPromptLang = 'en' | 'es';

export interface IdentityPromptProps {
  /** The custom-connection slug the answer is recorded against. */
  slug: string;
  /** Records the answer. Resolves true when it actually landed. */
  onSave: (slug: string, identity: string) => Promise<boolean>;
  /** Dismissed for this session — the caller decides how long that lasts. */
  onDismiss?: (slug: string) => void;
  lang?: IdentityPromptLang;
  /**
   * Visual register. The integrations page speaks panelTheme; onboarding has
   * its own brand tokens. The BEHAVIOUR is what must not drift, so the styling
   * fork is deliberately shallow and lives here rather than in two copies of
   * the logic.
   */
  variant?: 'panel' | 'onboarding';
}

const CHIP: Record<'panel' | 'onboarding', React.CSSProperties> = {
  panel: {
    background: 'none', border: '1px dashed #DDD8CF', borderRadius: 999,
    color: 'var(--brand-text-muted)', fontSize: '0.68rem', fontWeight: 600,
    padding: '0.1rem 0.45rem', cursor: 'pointer', textTransform: 'lowercase',
  },
  onboarding: {
    background: 'none', border: '1px dashed var(--brand-gold)', borderRadius: 999,
    color: '#6b5d3f', fontFamily: 'inherit', fontSize: '0.7rem', fontWeight: 600,
    padding: '0.1rem 0.5rem', cursor: 'pointer', textTransform: 'lowercase',
  },
};

export const IdentityPrompt: React.FC<IdentityPromptProps> = ({
  slug,
  onSave,
  onDismiss,
  lang = 'en',
  variant = 'panel',
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  // A save that resolves after the row is gone must not set state on a
  // component nobody is looking at.
  const aliveRef = useRef(true);
  React.useEffect(() => () => { aliveRef.current = false; }, []);

  const submit = useCallback(async () => {
    const identity = value.trim();
    // An empty submit is a dismissal, not a write: clearing an identity is a
    // deliberate act on a connection that HAS one, and this prompt only ever
    // appears on connections that do not.
    if (!identity) {
      setOpen(false);
      onDismiss?.(slug);
      return;
    }
    setSaving(true);
    setFailed(false);
    let ok = false;
    try {
      ok = await onSave(slug, identity);
    } catch {
      ok = false;
    }
    if (!aliveRef.current) return;
    setSaving(false);
    if (ok) {
      setOpen(false);
      return;
    }
    // Keep what they typed — making someone retype an address because our
    // write failed is our mistake charged to them.
    setFailed(true);
  }, [onSave, onDismiss, slug, value]);

  if (!open) {
    return (
      <button
        type="button"
        data-testid={`identity-ask-${slug}`}
        onClick={() => setOpen(true)}
        style={CHIP[variant]}
      >
        {IDENTITY_PROMPT_COPY.ask[lang]}
      </button>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minWidth: 0, flexWrap: 'wrap' }}>
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') { setOpen(false); onDismiss?.(slug); }
        }}
        placeholder={IDENTITY_PROMPT_COPY.placeholder[lang]}
        aria-label={IDENTITY_PROMPT_COPY.ask[lang]}
        data-testid={`identity-input-${slug}`}
        maxLength={120}
        style={{
          fontFamily: 'inherit', fontSize: '0.7rem', padding: '0.16rem 0.45rem',
          borderRadius: 999, border: '1px solid #DDD8CF', background: '#fff',
          color: '#3A3730', minWidth: 0, width: 170,
        }}
      />
      <button
        type="button"
        onClick={() => void submit()}
        disabled={saving}
        data-testid={`identity-save-${slug}`}
        style={{ ...CHIP[variant], borderStyle: 'solid', cursor: saving ? 'default' : 'pointer' }}
      >
        {saving ? IDENTITY_PROMPT_COPY.saving[lang] : IDENTITY_PROMPT_COPY.save[lang]}
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); onDismiss?.(slug); }}
        data-testid={`identity-cancel-${slug}`}
        style={{
          background: 'none', border: 'none', color: 'var(--brand-text-muted)', fontFamily: 'inherit',
          fontSize: '0.68rem', cursor: 'pointer', textDecoration: 'underline',
          textTransform: 'lowercase', padding: 0,
        }}
      >
        {IDENTITY_PROMPT_COPY.cancel[lang]}
      </button>
      {failed && (
        <span
          data-testid={`identity-error-${slug}`}
          style={{ fontSize: '0.66rem', color: '#B4443B', minWidth: 0 }}
        >
          {IDENTITY_PROMPT_COPY.failed[lang]}
        </span>
      )}
    </span>
  );
};

export default IdentityPrompt;
